import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy = () => {
  const lastUpdated = "March 15, 2026";
  const companyName = "The Motley Maker LLC";
  const appName = "AMSCompass";
  const contactEmail = "info@artisanmarketandstudio.com";
  const websiteUrl = "https://www.madeartisanmarkets.com";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-white/80 mt-2">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">1. Introduction</h2>
            <p>
              Welcome to {appName}, operated by {companyName} ("we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and services.
            </p>
            <p>
              By accessing or using {appName}, you agree to the terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, and business name when you create an account.</li>
              <li><strong>Profile Information:</strong> Business details, location, product categories, and preferences you provide in your profile.</li>
              <li><strong>Product Information:</strong> Details about your artisan products, including descriptions, pricing, materials, and images.</li>
              <li><strong>Event Information:</strong> Event applications, schedules, and related business data.</li>
              <li><strong>Financial Information:</strong> Sales records, pricing data, and fee calculations you enter into the system.</li>
              <li><strong>Communications:</strong> Messages, feedback, and support requests you send to us.</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">2.2 Information from Third-Party Services</h3>
            <p>When you connect third-party services to {appName}, we may collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Google Drive:</strong> When you connect your Google Drive account, we access your files in read-only mode to allow you to import images into your Media Library. We only access files you explicitly select. We store OAuth tokens securely to maintain your connection.</li>
              <li><strong>Dropbox:</strong> When you use Dropbox integration, we receive file metadata and download URLs for files you select to import.</li>
              <li><strong>Payment Processors:</strong> If you subscribe to paid features, our payment processor (Stripe) handles your payment information. We do not store your full credit card details.</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">2.3 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, features used, and actions taken within the application.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs.</li>
              <li><strong>Cookies:</strong> We use essential cookies for authentication and session management.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process your transactions and manage your account</li>
              <li>Send you service-related communications and updates</li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Personalize your experience and provide relevant recommendations</li>
              <li>Analyze usage patterns to improve our application</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">4. Google API Services - Limited Use Disclosure</h2>
            <p>
              {appName}'s use and transfer of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <p className="mt-4">Specifically:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We only request access to the Google Drive data necessary to provide the image import feature.</li>
              <li>We use Google Drive data solely to import images you select into your {appName} Media Library.</li>
              <li>We do not use Google Drive data for advertising purposes.</li>
              <li>We do not sell Google Drive data to third parties.</li>
              <li>We do not use Google Drive data to determine creditworthiness or for lending purposes.</li>
              <li>Human access to Google Drive data is limited to what is necessary for support and debugging, with your consent.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">5. Information Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> With third-party vendors who assist us in providing our services (hosting, analytics, payment processing), subject to confidentiality obligations.</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, or other legal process.</li>
              <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property, or that of our users or others.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to you.</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit using TLS/SSL</li>
              <li>Secure storage of authentication credentials</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls limiting employee access to user data</li>
              <li>Secure OAuth token storage for third-party integrations</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">7. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide you services. We may retain certain information as required by law or for legitimate business purposes, such as:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Resolving disputes and enforcing our agreements</li>
              <li>Complying with legal and regulatory requirements</li>
              <li>Preventing fraud and abuse</li>
            </ul>
            <p className="mt-4">
              You may request deletion of your account and associated data by contacting us at {contactEmail}.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">8. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal obligations.</li>
              <li><strong>Portability:</strong> Request a portable copy of your data.</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for processing where consent is the legal basis.</li>
              <li><strong>Disconnect Services:</strong> Revoke access to connected third-party services (Google Drive, Dropbox) at any time through your Settings.</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at {contactEmail}.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">9. Third-Party Links and Services</h2>
            <p>
              Our application may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">10. Children's Privacy</h2>
            <p>
              {appName} is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we learn we have collected such information, we will delete it promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p><strong>{companyName}</strong></p>
              <p>Email: <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a></p>
              <p>Website: <a href={websiteUrl} className="text-blue-600 hover:underline">{websiteUrl}</a></p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1E3A5F] text-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/80">
            &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/terms" className="text-white/80 hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-white/80 hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
